/**
 * Turning a transcript into burnable captions.
 *
 * Whisper hands back a list of spoken segments, each with a start, an end and
 * some text, measured from the start of the clip's own audio. Reaction clips
 * are short and vertical-friendly, so captions have to be readable at a glance:
 * two lines at most, on screen long enough to read, gone before the next line
 * starts. None of that is ffmpeg's job — it is arithmetic and string work, easy
 * to get subtly wrong, so it is pure and tested here. The worker only extracts
 * the audio, runs the transcriber, writes the file this produces and burns it in.
 */
/**
 * How a search result marks the words that matched.
 *
 * Not <b></b>, which is what ts_headline would use by default: the snippet is
 * built from a transcript and rendered in the dashboard, and a server that
 * hands a client HTML to inject will eventually hand it a transcript
 * containing a script tag. These two control characters cannot occur in
 * transcribed speech and mean nothing to HTML, JSON or SQL, so the client
 * splits on them and styles the pieces itself -- nothing from a transcript is
 * ever parsed as markup.
 *
 * Here rather than in the route because it is a contract between two programs:
 * the server writes them and the dashboard reads them, and a marker defined
 * twice is a marker that will eventually be defined differently.
 */
export const SNIPPET_START = "\u0001";
export const SNIPPET_END = "\u0002";
/**
 * The spoken words as one flat string, for search.
 *
 * Separate from the cues on purpose. A cue is shaped for reading on screen --
 * wrapped to a line length, split at a pause, timed to a frame. None of that
 * helps somebody typing a half-remembered phrase into a search box, and the
 * line wrapping actively hurts: a phrase broken across two cues would never
 * match if the cue text were indexed as written.
 *
 * Whitespace is collapsed for the same reason. Transcribers emit leading
 * spaces, double spaces and the occasional newline inside a segment, and two
 * clips that said the same sentence should produce the same searchable text
 * regardless of which transcriber was deployed when they were rendered.
 */
export function transcriptText(segments) {
    return segments
        .map((s) => (typeof s?.text === "string" ? s.text : ""))
        .join(" ")
        .replace(/\s+/g, " ")
        .trim();
}
/**
 * Reads segments out of a transcriber's JSON, whichever one is deployed.
 *
 * whisper.cpp writes `transcription: [{ offsets: {from,to}, text }]` with
 * millisecond offsets; OpenAI's whisper writes `segments: [{ start, end, text }]`
 * in seconds. Both are accepted so the backend can be swapped without touching
 * the render. Anything unparseable yields no segments rather than throwing — a
 * clip with no captions is fine; a crashed render is not.
 */
export function parseWhisperJson(raw) {
    let doc;
    try {
        doc = JSON.parse(raw);
    }
    catch {
        return [];
    }
    if (!doc || typeof doc !== "object")
        return [];
    const obj = doc;
    if (Array.isArray(obj.transcription)) {
        return obj.transcription
            .map((seg) => {
            const off = (seg.offsets ?? {});
            return {
                start_ms: Number(off.from ?? 0),
                end_ms: Number(off.to ?? 0),
                text: String(seg.text ?? ""),
            };
        })
            .filter((s) => Number.isFinite(s.start_ms) && Number.isFinite(s.end_ms));
    }
    if (Array.isArray(obj.segments)) {
        return obj.segments
            .map((seg) => ({
            start_ms: Math.round(Number(seg.start ?? 0) * 1000),
            end_ms: Math.round(Number(seg.end ?? 0) * 1000),
            text: String(seg.text ?? ""),
        }))
            .filter((s) => Number.isFinite(s.start_ms) && Number.isFinite(s.end_ms));
    }
    return [];
}
const DEFAULTS = { maxCharsPerLine: 38, maxLines: 2, minCueMs: 900 };
/** Greedy word wrap. A single word longer than the width gets its own line. */
export function wrapWords(text, maxChars) {
    const words = text.trim().split(/\s+/).filter(Boolean);
    const lines = [];
    let line = "";
    for (const w of words) {
        if (!line)
            line = w;
        else if (line.length + 1 + w.length <= maxChars)
            line += " " + w;
        else {
            lines.push(line);
            line = w;
        }
    }
    if (line)
        lines.push(line);
    return lines;
}
/**
 * Normalises raw transcript segments into clean, non-overlapping cues.
 *
 * - empty or whitespace-only segments are dropped, not shown as blank cards;
 * - everything is clamped into the clip window, and segments entirely outside
 *   it disappear;
 * - a cue never outlives the next cue's start — a caption that lingers over the
 *   following line is worse than one that leaves a beat early;
 * - too-short cues are stretched to a readable minimum, but never into the next;
 * - a segment too long for maxLines is split into several cues sharing its time,
 *   so a monologue scrolls rather than overflowing off-screen.
 */
export function toCues(segments, opts) {
    const maxChars = opts.maxCharsPerLine ?? DEFAULTS.maxCharsPerLine;
    const maxLines = opts.maxLines ?? DEFAULTS.maxLines;
    const minCue = opts.minCueMs ?? DEFAULTS.minCueMs;
    const dur = opts.clipDurationMs;
    // Clean, clamp, drop the empties and the out-of-window ones, in time order.
    const clean = segments
        .map((s) => ({
        start_ms: Math.max(0, Math.min(s.start_ms, dur)),
        end_ms: Math.max(0, Math.min(s.end_ms, dur)),
        text: s.text.trim(),
    }))
        .filter((s) => s.text && s.end_ms > s.start_ms)
        .sort((a, b) => a.start_ms - b.start_ms);
    const cues = [];
    for (let i = 0; i < clean.length; i++) {
        const seg = clean[i];
        const next = clean[i + 1];
        const nextStart = next ? next.start_ms : dur;
        // A caption leaves when the next one is due, so two never stack. Give it the
        // readable minimum when there is room, but the next cue's start always wins.
        let end = Math.max(seg.end_ms, Math.min(seg.start_ms + minCue, dur));
        if (end > nextStart)
            end = nextStart;
        if (end <= seg.start_ms)
            continue;
        const lines = wrapWords(seg.text, maxChars);
        // Fits in one card.
        if (lines.length <= maxLines) {
            cues.push({ start_ms: seg.start_ms, end_ms: end, lines });
            continue;
        }
        // Too long: split into equal-time cards of maxLines each, across [start,end].
        const span = end - seg.start_ms;
        const cards = Math.ceil(lines.length / maxLines);
        for (let c = 0; c < cards; c++) {
            cues.push({
                start_ms: Math.round(seg.start_ms + (span * c) / cards),
                end_ms: Math.round(seg.start_ms + (span * (c + 1)) / cards),
                lines: lines.slice(c * maxLines, c * maxLines + maxLines),
            });
        }
    }
    return cues;
}
function pad(n, w = 2) {
    return String(n).padStart(w, "0");
}
/** HH:MM:SS.mmm for WebVTT. */
function vttTime(ms) {
    const t = Math.max(0, Math.round(ms));
    const h = Math.floor(t / 3600000);
    const m = Math.floor(t / 60000) % 60;
    const s = Math.floor(t / 1000) % 60;
    return `${pad(h)}:${pad(m)}:${pad(s)}.${pad(t % 1000, 3)}`;
}
/** H:MM:SS.cc (centiseconds) for ASS. */
function assTime(ms) {
    const t = Math.max(0, Math.round(ms));
    const h = Math.floor(t / 3600000);
    const m = Math.floor(t / 60000) % 60;
    const s = Math.floor(t / 1000) % 60;
    const cs = Math.round((t % 1000) / 10);
    return `${h}:${pad(m)}:${pad(s)}.${pad(cs)}`;
}
/** A downloadable sidecar, for editors that re-time or restyle. */
export function cuesToVtt(cues) {
    const blocks = cues.map((c) => `${vttTime(c.start_ms)} --> ${vttTime(c.end_ms)}\n${c.lines.join("\n")}`);
    return `WEBVTT\n\n${blocks.join("\n\n")}\n`;
}
/**
 * ASS for burn-in. Styled here rather than via ffmpeg's force_style so the look
 * travels with the file: bold, white, a heavy black outline so it survives any
 * background, sat above the lower third where overlays and platform UI sit.
 */
export function cuesToAss(cues, style = {}) {
    const playResY = style.playResY ?? 1080;
    const playResX = Math.round((playResY * 16) / 9);
    const font = style.fontName ?? "Arial";
    const size = style.fontSize ?? Math.round(playResY / 15);
    const header = `[Script Info]\n` +
        `ScriptType: v4.00+\n` +
        `PlayResX: ${playResX}\n` +
        `PlayResY: ${playResY}\n` +
        `WrapStyle: 2\n\n` +
        `[V4+ Styles]\n` +
        `Format: Name, Fontname, Fontsize, PrimaryColour, OutlineColour, BackColour, ` +
        `Bold, BorderStyle, Outline, Shadow, Alignment, MarginL, MarginR, MarginV\n` +
        // &H00FFFFFF white, &H00000000 black outline; Alignment 2 = bottom-centre;
        // MarginV lifts it clear of the very bottom edge.
        `Style: Cap,${font},${size},&H00FFFFFF,&H00000000,&H00000000,-1,1,` +
        `${Math.max(2, Math.round(size / 12))},0,2,60,60,${Math.round(playResY / 9)}\n\n` +
        `[Events]\n` +
        `Format: Layer, Start, End, Style, Name, MarginL, MarginR, MarginV, Effect, Text\n`;
    const events = cues
        .map((c) => `Dialogue: 0,${assTime(c.start_ms)},${assTime(c.end_ms)},Cap,,0,0,0,,` +
        c.lines.map(assText).join("\\N"))
        .join("\n");
    return header + events + "\n";
}
/** Inside a Dialogue line, braces open override blocks and backslash is special. */
function assText(line) {
    return line.replace(/\\/g, "\u2216").replace(/\{/g, "(").replace(/\}/g, ")");
}
/**
 * The ffmpeg `subtitles` filter arg for a file on disk.
 *
 * This is the Windows footgun. The filtergraph parser treats `:` as the option
 * separator and `\` as an escape, so a raw `C:\Users\…\sub.ass` is read as the
 * option `C` with nonsense after it. Forward slashes sidestep the backslash
 * problem entirely, and the drive colon is escaped so it is read as part of the
 * filename rather than the start of the next option.
 */
export function escapeSubtitlesPath(path) {
    return path
        .replace(/\\/g, "/") // C:\a\b -> C:/a/b  (no backslashes left to escape)
        .replace(/:/g, "\\:") // C:/a/b -> C\:/a/b (colon is now part of the name)
        .replace(/'/g, "\\'");
}
/** The full `-vf`/filter value that burns an ASS file into the video. */
export function subtitlesFilter(assPath) {
    return `subtitles=${escapeSubtitlesPath(assPath)}`;
}
//# sourceMappingURL=captions.js.map